import {ShowMore} from '@github-ui/disclosure'
import {
  isPendingProjectItemId,
  useUpdateIssueProjectsMutation,
} from '@github-ui/item-picker/useUpdateIssueProjectsMutation'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import type {QueryKey} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {type HTMLAttributes, type ReactNode, useCallback, useMemo} from 'react'

import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {useDeleteIssueProjectsMutation} from '../../mutations/delete-issue-projects-mutation'
import type {IssueNode, ProjectsSectionProjectItemNode} from '../issue-viewer-page-query-types'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {SingleSelectFieldConfigFragment, SingleSelectFieldFragment} from './fields/SingleSelectField'
import {ProjectItemSection} from './ProjectItemSection'
import styles from './ProjectsSection.module.css'

const MAX_VISIBLE_ITEMS = 5

export const ReadonlyProjectsSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.projects} />
export const ProjectsSectionHeader = ({
  ref,
  ...props
}: HTMLAttributes<HTMLElement> & {ref?: React.Ref<HTMLButtonElement>}) => (
  <SectionHeader ref={ref} id={props.id} title={LABELS.sectionTitles.projects} buttonProps={props} />
)
ProjectsSectionHeader.displayName = 'ProjectsSectionHeader'

type ProjectsSectionProps = {
  sectionHeader: React.ReactElement
  hasItems: boolean
  children?: ReactNode
}
export const ProjectsSection = ({sectionHeader, hasItems, children}: ProjectsSectionProps) => {
  return (
    <Section
      id={`sidebar-projects-section`}
      sectionHeader={sectionHeader}
      emptyText={hasItems ? undefined : LABELS.emptySections.projects}
    >
      {children}
    </Section>
  )
}

export const ProjectsSectionFallback = () => (
  <Section
    id={'projects-section-fallback'}
    sectionHeader={<ReadonlySectionHeader title={ERRORS.projectsUnavailable} />}
    emptyText={ERRORS.tryAgainLater}
  />
)

export type EditIssueProjectsSectionProps = {
  issueOrPullRequest: ProjectsSectionFragment
  viewerCanUpdateMetadata?: boolean | null
  selectedProjectId?: string
  allowedProjectOwner?: string
  onIssueUpdate: () => void
  onRemovedFromProject: () => void
  insideSidePanel?: boolean
  readonly: boolean
  settlementQueryKey?: QueryKey
  fieldValueQueryKeys?: readonly QueryKey[]
}

type ProjectsSectionProjectNode = Pick<
  ProjectsSectionProjectItemNode['project'],
  'id' | 'title' | 'closed' | 'number' | 'url' | 'template' | 'viewerCanUpdate' | 'hasReachedItemsLimit'
> & {
  field:
    | (SingleSelectFieldConfigFragment &
        Pick<
          Extract<
            NonNullable<ProjectsSectionProjectItemNode['project']['field']>,
            {__typename: 'ProjectV2SingleSelectField'}
          >,
          'databaseId'
        >)
    | null
}

type ProjectsSectionProjectItem = Pick<ProjectsSectionProjectItemNode, 'id' | 'isArchived'> & {
  project: ProjectsSectionProjectNode
  fieldValueByName: SingleSelectFieldFragment | null
}

export type ProjectsSectionFragment = Pick<IssueNode, 'id' | 'number'> & {
  repository: Pick<IssueNode['repository'], 'name' | 'isArchived'> & {
    owner: Pick<IssueNode['repository']['owner'], 'login'>
  }
  projectItems: {
    edges: ReadonlyArray<{node: ProjectsSectionProjectItem | null} | null> | null
  } | null
}

export function EditIssueProjectsSection(props: EditIssueProjectsSectionProps) {
  const projectsSectionData = props.issueOrPullRequest

  if (!projectsSectionData.projectItems) return <ProjectsSectionFallback />

  return <EditIssueProjectsSectionInternal projectsSectionData={projectsSectionData} {...props} />
}

type EditIssueProjectsSectionInternalType = {
  projectsSectionData: ProjectsSectionFragment
} & EditIssueProjectsSectionProps

const EditIssueProjectsSectionInternal = ({
  projectsSectionData,
  viewerCanUpdateMetadata,
  onIssueUpdate,
  onRemovedFromProject,
  insideSidePanel,
  selectedProjectId,
  readonly,
  settlementQueryKey,
  fieldValueQueryKeys,
}: EditIssueProjectsSectionInternalType) => {
  const {addToast} = useToastContext()
  const allProjectItems = (projectsSectionData.projectItems?.edges ?? [])
    .flatMap(edge => (edge?.node ? edge?.node : []))
    .filter(projectItem => projectItem.project != null)
  const projectItems = allProjectItems.filter(projectItem => projectItem.project.closed !== true)
  const closedProjectItems = allProjectItems.filter(projectItem => projectItem.project.closed === true)

  // Auto-expand when the selected project (e.g. from Projects board side panel) is beyond the visible slice
  // or is in a closed project
  const selectedProjectIsHidden = useMemo(() => {
    if (selectedProjectId === undefined) return false
    const isInClosedProject = closedProjectItems.some(item => item.project.id === selectedProjectId)
    if (isInClosedProject) return true
    return (
      projectItems.length > MAX_VISIBLE_ITEMS &&
      projectItems.findIndex(item => item.project.id === selectedProjectId) >= MAX_VISIBLE_ITEMS
    )
  }, [projectItems, closedProjectItems, selectedProjectId])

  const {repository} = projectsSectionData

  const readonlyProject = readonly || !viewerCanUpdateMetadata || repository?.isArchived

  const issueId = projectsSectionData.id || ''
  const hasProjectItems = projectItems.length > 0 || closedProjectItems.length > 0

  const updateIssueProjects = useUpdateIssueProjectsMutation({
    owner: projectsSectionData.repository.owner.login,
    repo: projectsSectionData.repository.name,
    issueNumber: projectsSectionData.number,
    issueId,
    settlementQueryKey,
  })
  const deleteIssueProject = useDeleteIssueProjectsMutation({
    owner: projectsSectionData.repository.owner.login,
    repo: projectsSectionData.repository.name,
    issueNumber: projectsSectionData.number,
    issueId,
    settlementQueryKey,
  })
  const saveProjects = useCallback(
    (selectedProjects: ProjectPickerProject[]) => {
      const projectsToAdd = selectedProjects.filter(project => !projectItems.some(p => p.project.id === project.id))

      const projectsToRemove = projectItems.filter(project => !selectedProjects.some(p => p.id === project.project.id))

      let hasError = false
      const showError = () => {
        if (hasError) return
        hasError = true
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: ERRORS.couldNotUpdateProjects})
      }

      for (const project of projectsToAdd) {
        updateIssueProjects(project, {
          onError: showError,
          onCompleted: onIssueUpdate,
        })
      }

      for (const project of projectsToRemove) {
        deleteIssueProject(project.id, {
          onError: showError,
          onCompleted: () => {
            const isRemovingFromSelectedProject = project.project.id === selectedProjectId
            if (isRemovingFromSelectedProject) {
              onRemovedFromProject()
            }
          },
        })
      }
    },
    [
      addToast,
      deleteIssueProject,
      onIssueUpdate,
      onRemovedFromProject,
      projectItems,
      selectedProjectId,
      updateIssueProjects,
    ],
  )

  const selectedProjects = useMemo(() => projectItems.map(projectItem => projectItem.project), [projectItems])

  const sectionHeader = useMemo(() => {
    if (!projectsSectionData.repository || readonlyProject) return <ReadonlyProjectsSectionHeader />

    return (
      <ProjectPicker
        pickerId={`edit-issue-projects-picker-${issueId}`}
        repo={projectsSectionData.repository.name}
        owner={projectsSectionData.repository.owner.login}
        selectedProjects={selectedProjects}
        readonly={readonlyProject}
        onSave={saveProjects}
        anchorElement={props => <ProjectsSectionHeader {...props} />}
        insidePortal={insideSidePanel}
      />
    )
  }, [issueId, projectsSectionData.repository, readonlyProject, saveProjects, selectedProjects, insideSidePanel])

  const orderedProjectItems = [...projectItems, ...closedProjectItems]
  const initialProjectCount = Math.min(MAX_VISIBLE_ITEMS, projectItems.length)
  const hiddenProjectCount = orderedProjectItems.length - initialProjectCount

  if (!projectsSectionData.repository || !projectsSectionData.number) return null

  return (
    <ProjectsSection sectionHeader={sectionHeader} hasItems={hasProjectItems}>
      <ShowMore
        items={orderedProjectItems}
        initialCount={initialProjectCount}
        defaultOpen={selectedProjectIsHidden}
        getItemKey={projectItem => projectItem.id}
        renderItem={projectItem => (
          <ProjectItemSection
            onIssueUpdate={onIssueUpdate}
            projectItem={projectItem}
            isPending={isPendingProjectItemId(projectItem.id)}
            isOpen={selectedProjectId === projectItem.project.id}
            isProjectClosed={projectItem.project.closed}
            owner={projectsSectionData.repository.owner.login}
            repo={projectsSectionData.repository.name}
            issueNumber={projectsSectionData.number}
            fieldValueQueryKeys={fieldValueQueryKeys}
          />
        )}
        moreLabel={LABELS.showMoreProjects(hiddenProjectCount, closedProjectItems.length)}
        lessLabel={LABELS.showLessProjects}
        className={styles.projectItemsContainer}
        listClassName={styles.projectItemsList}
        overflowItemClassName={styles.overflowProjectItem}
        triggerBlock
        triggerAlignContent="start"
      />
    </ProjectsSection>
  )
}
